import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { HttpClientModule } from '@angular/common/http';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { ConfirmationPopoverModule } from 'angular-confirmation-popover';
import { NgxSliderModule } from '@angular-slider/ngx-slider';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatInputModule } from '@angular/material/input';
import { MatSliderModule } from '@angular/material/slider';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatMenuModule } from '@angular/material/menu';
import { MatTabsModule } from '@angular/material/tabs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatIconModule } from '@angular/material/icon';
import { NgClickOutsideDirective } from 'ng-click-outside2';
import { QuillModule } from 'ngx-quill';
import { NgxApexchartsModule } from "ngx-apexcharts";

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DataService } from './shared/network/data.service';
import { L10nService } from './shared/l10n.service';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { GlobalErrorHandler } from './globalerrorhandler';
import { LoginComponent } from './account/login.component';
import { AccountService } from './shared/account/account-service';
import { ProjectService } from './shared/project/project.service';
import { ControlColumnSelectComponent } from './import/column-types/control/control-column-selection.component';
import { OutcomeColumnSelectComponent } from './import/column-types/outcome/outcome-column-selection.component';
import { NewLostColumnSelectComponent } from './import/column-types/new-lost/new-lost-column-selection.component';
import { NotificationService } from './shared/notificaton.service';
import { DimensionsColumnsSelectComponent } from './import/column-types/dimensions/dimensions-columns-selection.component';
import { ProjectListComponent } from './projects/project-list.component';
import { PreaggregationStrategySelectComponent } from "./import/aggregation/preaggregation-strategy.component";
import { AggregationSevice } from './shared/preaggregation/aggregation-service';
import { CardsComponent } from './cards/cards.component';
import { HeaderComponent } from './header/header.component';
import { AutoFocusOnShowDirective } from './shared/directives/auto-focus-and-select';
import { RangeSlider } from './range-slider/range-slider.component';
import { DisplayService } from './shared/display-service';
import { ComplexityMgntComponent } from './import/complexity-mgnt/complexity-mgnt.component';
import { ComplexityMngtService } from './shared/complexity-mgnt/complexity-mngt.service';
import { ImportProgressService } from './shared/import/import-progress.service';
import { CalcOptionsComponent } from './import/column-types/reference/calc-options.component';
import { AutofocusDirective } from './autofocus-directive';
import { SignalRService } from './shared/network/signalr.service';
import { HelpLinkComponent } from './shared/help-link/help-link.component';
import { HelpLinkService } from './shared/help-link/help-link.service'
import { SeriesComponent } from './import/series/series.component';
import { FancyNumberPipe } from './shared/directives/fancy-number.pipe';
import { HoverClassDirective } from './shared/directives/hover-class.directive';
import { DimensionLabel } from './cards/dimension-label.component';
import { GoogleSettingsService } from './import/upload/google-settings.service';
import { CustomLinkModule } from './shared/quill/quill-custom-link-module';
import { ProjectOverviewComponent } from './cards/project-overview.component';
import { TopValsModalComponent } from './import/complexity-mgnt/top-vals-modal.component';
import { ChangesModalComponent } from './import/aggregation/changes-modal.component';
import { RenameModalComponent } from './import/series/rename-modal.component';
import { RedundantModalComponent } from './import/column-types/dimensions/redundant-modal.component';
import { ArticlesViewComponent } from './articles-view/articles-view.component';
import { UploadFileModalComponent } from './import/upload/upload-file-modal.component';
import { DeleteTagModalComponent } from './import/upload/delete-tag-modal.component';
import { ImportWizardComponent } from './import/import-wizard.component';
import { FormulaInputComponent } from './import/column-types/outcome/formula-input.component';
import { AggregationSettingsComponent } from './import/aggregation/aggregation-settings.component';
import { FilterByPropertyPipe } from './shared/filter-by-property.pipe ';
import { RelativeImpactOptionsComponent } from './import/column-types/reference/relative-impact-options.component';
import { MaxMultStorageService } from './import/complexity-mgnt/max-mult-storage.service';
import { ForecastComponent } from './import/series/forecast.component';
import { MainMenuComponent } from './header/main-menu.component';
import { TabsTreeViewComponent } from './cards/tabs-tree-view.component';
import { FilterByPropertyNotPipe } from './shared/filter-by-property-not.pipe';
import { SettingsModalComponent } from './cards/settings-modal.component';
import { EditFilterExpressionModalComponent } from './cards/edit-filter-expression-modal.component';
import { RenameViewModalComponent } from './cards/rename-view-modal.component';
import { InitialImportProgressComponent } from './cards/initial-import-progress.component';
import { ColumnScrollService } from './import/column-scroll.service';
import { RelatedCardsComponent } from './cards/related/related-cards.component';
import { RelatedCardsModalComponent } from './cards/related/related-cards-modal.component';
import { RelatedCardsSettingsModalComponent } from './cards/related/related-cards-settings-modal.component';
import { TrendComponent } from './cards/trend/trend.component';
import { TrendModalComponent } from './cards/trend/trend-modal.component';


@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    ControlColumnSelectComponent,
    OutcomeColumnSelectComponent,
    NewLostColumnSelectComponent,
    DimensionsColumnsSelectComponent,
    ProjectListComponent,
    PreaggregationStrategySelectComponent,
    CardsComponent,
    HeaderComponent,
    AutoFocusOnShowDirective,
    RangeSlider,
    ComplexityMgntComponent,
    CalcOptionsComponent,
    RelativeImpactOptionsComponent,
    AutofocusDirective,
    HelpLinkComponent,
    SeriesComponent,
    FancyNumberPipe,
    HoverClassDirective,
    DimensionLabel,
    ProjectOverviewComponent,
    TopValsModalComponent,
    ChangesModalComponent,
    RenameModalComponent,
    RedundantModalComponent,
    ArticlesViewComponent,
    UploadFileModalComponent,
    DeleteTagModalComponent,
    ImportWizardComponent,
    FormulaInputComponent,
    AggregationSettingsComponent,
    FilterByPropertyPipe,
    FilterByPropertyNotPipe,
    ForecastComponent,
    MainMenuComponent,
    TabsTreeViewComponent,
    SettingsModalComponent,
    EditFilterExpressionModalComponent,
    RenameViewModalComponent,
    InitialImportProgressComponent,
    RelatedCardsComponent,
    RelatedCardsModalComponent,
    RelatedCardsSettingsModalComponent,
    TrendComponent,
    TrendModalComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    DragDropModule,
    FormsModule,
    ReactiveFormsModule,
    ToastrModule.forRoot(),
    ConfirmationPopoverModule.forRoot({
        confirmButtonType: 'danger', // set defaults here
    }),
    NgxSliderModule,
    MatGridListModule,
    MatInputModule,
    MatSliderModule,
    MatSelectModule,
    MatButtonModule,
    MatDialogModule,
    MatMenuModule,
    MatTabsModule,
    MatAutocompleteModule,
    MatChipsModule,
    MatIconModule,
    NgClickOutsideDirective,
    QuillModule.forRoot({customModules: [{
        implementation: CustomLinkModule,
        path: 'modules/customLink'
      }]}),
    NgxApexchartsModule,
  ],
  exports: [

  ],
  providers: [
    DataService,
    L10nService,
    NotificationService,
    AccountService,
    ProjectService,
    AggregationSevice,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler
    },
    DisplayService,
    ComplexityMngtService,
    ImportProgressService,
    SignalRService,
    HelpLinkService,
    GoogleSettingsService,
    MaxMultStorageService,
    ColumnScrollService,
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
